import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import BasicDialog from "../../../components/base/BasicDialog";
import { FilterTableWrapper } from "../../../components/table/BetterFilterTable/BetterFilterTable";
import LoaderWrapper from "../../../components/wrapper/LoaderWrapper";
import usePaginationWithLocalSearchParamsAndOrdering from "../../../hooks/usePaginationWithLocalSearchParamsAndOrdering";
import { TEXT_FIELD_TYPE, DATE_RANGE_FIELD_TYPE, AUTOCOMPLETE_FIELD_TYPE } from "../../../helpers/constants";
import {Checkbox, Button , Grid} from "@mui/material";
import { useBankings, useBankingFilteringData } from "../../../services/bankingService";
import DateService from "../../../services/dateService";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import { AddBankingDialog } from "../../FinancialPanelBankingPage/AddBankingDialog";
import { cleanObject,getObjectFromSearchParams } from "../../../helpers/methods";

export function SelectBankingForAgreementDialog(props) {
    const { t } = useTranslation();

    const [bankingsDataLocal, setBankingsDataLocal] = useState([]);
    const [bankingsCountLocal, setBankingsCountLocal] = useState(0);

    const pagination = usePaginationWithLocalSearchParamsAndOrdering();

    const agreeemntSearchParams = useMemo(() => {
        const params = new URLSearchParams(cleanObject(getObjectFromSearchParams(pagination.searchParams)));
        params.set("page", String(pagination.page));
        params.set("page_size", String(pagination.pageSize));
        if (props.investmentId) {
            params.set("banking_invest", props.investmentId)
        }
        params.set("banking_active", true)
        params.set("exclude_merged_bankings", true)
        params.set("only_investment_bankings", true)
        params.set("banking_is_withdrawal", props.isBankingForWithdrawal)
        return params;
    }, [pagination.searchParams, pagination.page, pagination.pageSize, props.isBankingForWithdrawal]);

    const { data: bankingsData } = useBankings(agreeemntSearchParams);

    useEffect(() => {
        if (bankingsData?.results) {
            setBankingsDataLocal(bankingsData?.results);
            setBankingsCountLocal(bankingsData.count);
        }
    }, [bankingsData]);

    const { data: filteringData, isLoading: isFilteringDataLoading } = useBankingFilteringData()

    const [selectedBanking, setSelectedBanking] = useState()
    const handleSelectBanking = (banking) => {
        setSelectedBanking(banking)
    };

    const handleUnselectBanking = () => {
        setSelectedBanking(null)
    };

    const handleSubmit = () => {
        props.onSubmit(selectedBanking)
        props.onClose()
    }

    const { openDialog: openAddBankingDialog } = useDialogBetter();
        
    const handleOpenAddBankingDialog = (e) => {
        openAddBankingDialog({
            Component: AddBankingDialog,
            componentProps: {
                onAddBankingCallback: (response) => {
                    props.onSubmit(response.data)
                    props.onClose()
            },
                isBankingForWithdrawal: props.isBankingForWithdrawal
            }
        });
    };

    return (
        <BasicDialog
            open={true}
            onClose={() => props.onClose()}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("bankings.mergeBankingWithAgreement")}
            maxWidth={"lg"}
            showDialogActions
            alwaysFullScreen={false}
            contentSx={{ pt: 0 }}
        >
            <Grid container justifyContent={"flex-end"}>
                <Button variant="contained" onClick={handleOpenAddBankingDialog} sx={{marginBlock: 2}}>
                    {t("bankings.add_banking")}
                </Button>
                <LoaderWrapper showLoader={isFilteringDataLoading || !bankingsDataLocal}>
                    <FilterTableWrapper
                        data={bankingsDataLocal}
                        countRecords={bankingsCountLocal}
                        localPagination={pagination}
                        filtersOpts={{
                            showCleanFilterIcon: true,
                        }}
                        tableStyle={{
                            maxHeight: "80vh",
                            overflow: "auto",
                        }}
                        onSearchFiltersChange={pagination.handleChangeSearchParams}
                        fieldsConfig={[
                            {
                                name: "checkboox",
                                filterType: null,
                                label: t("select"),
                                getValueCallback: (row) => (
                                    <Checkbox
                                        checked={selectedBanking?.id===row.id}
                                        onChange={(e) => {
                                            if (e.currentTarget.checked) {
                                                handleSelectBanking(row);
                                            } else {
                                                handleUnselectBanking(row);
                                            }
                                        }}
                                    />
                                ),
                            },
                            {
                                name: "banking_name",
                                getValueCallback: (rowData) => rowData.banking_name,
                                label: t("bankings.banking_name"),
                                filterType: TEXT_FIELD_TYPE,
                                sortable: true
                            },
                            {
                                name: "created",
                                getValueCallback: (rowData) =>
                                    DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.created),
                                label: t("bankings.created"),
                                filterType: DATE_RANGE_FIELD_TYPE,
                                getFilterFieldConfigCallback: (filteringData) => ({
                                    style: { minWidth: "100px" },
                                }),
                                sortable: true,
                            },
                            {
                                name: "banking_invest",
                                getValueCallback: (rowData) => rowData.banking_invest?.investment_name_sales,
                                label: t("bankings.banking_invest"),
                                filterType: AUTOCOMPLETE_FIELD_TYPE,
                                fieldConfig: {
                                    optionLabelKey: "investment_name_sales",
                                    options: filteringData?.investment,
                                    isObjectOption: true,
                                    disableClearable: true,
                                    multiple: false
                                },
                            },
                            {
                                name: "banking_bank",
                                getValueCallback: (rowData) => rowData.banking_bank,
                                label: t("bankings.banking_bank"),
                                filterType: AUTOCOMPLETE_FIELD_TYPE,
                                fieldConfig: {
                                    optionLabelKey: "bank_name",
                                    options: filteringData?.bank,
                                    multiple: false,
                                    isObjectOption: true,
                                    disableClearable: true
                                },
                            },
                            {
                                name: "banking_kind",
                                getValueCallback: (rowData) => rowData.banking_kind,
                                label: t("bankings.banking_kind"),
                                filterType: AUTOCOMPLETE_FIELD_TYPE,
                                fieldConfig: {
                                    optionLabelKey: "bankingkind_name",
                                    options: filteringData?.banking_kind,
                                    isObjectOption: true,
                                    disableClearable: true,
                                    multiple: false
                                },
                            },
                            {
                                name: "banking_nr",
                                getValueCallback: (rowData) => rowData.banking_nr,
                                label: t("bankings.banking_nr"),
                                filterType: TEXT_FIELD_TYPE,
                                sortable: true,
                            },
                        ]}
                    />
                </LoaderWrapper>
                <LoadingButton
                    loading={false}
                    // disabled={!selectedBanking}
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={handleSubmit}
                    fullWidth
                >
                    {t("select")}
                </LoadingButton>
            </Grid>
        </BasicDialog>
    );
}
